import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StatusBadge from '../components/StatusBadge';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, ChevronRight, Phone, Mail, MapPin, Calendar as CalendarIcon, AlertTriangle, FileText, Sparkles, Wind, Thermometer } from 'lucide-react';

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customers, devices, alerts, tickets, technicians, plans } = useApp();
  const c = customers.find((x) => x.id === id);

  if (!c) {
    return (
      <div className="text-center py-20">
        <div className="text-slate-500">Customer not found.</div>
        <Button className="mt-4" onClick={() => navigate('/customers')}>Back</Button>
      </div>
    );
  }

  const custDevices = devices.filter((d) => d.customerId === c.id);
  const custAlerts = alerts.filter((a) => a.customerId === c.id);
  const custTickets = tickets.filter((t) => t.customerId === c.id);
  const tech = technicians.find((t) => t.id === c.preferredTechId);
  const plan = plans.find((p) => p.id === c.planId);

  const invoices = [
    { id: 'INV-2418', date: '2025-11-18', desc: 'Preventive maintenance visit', amount: 189, status: 'Paid' },
    { id: 'INV-2311', date: '2025-08-02', desc: 'Capacitor replacement', amount: 285, status: 'Paid' },
    { id: 'INV-2190', date: '2025-05-14', desc: 'Spring tune-up', amount: 149, status: 'Paid' },
  ];

  const upsells = [
    { label: 'Media filter upgrade', value: '$310', icon: Wind },
    { label: 'IAQ assessment', value: '$189', icon: Sparkles },
    { label: 'Smart zoning consultation', value: '$0', icon: Thermometer },
  ];

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <button onClick={() => navigate('/customers')} className="hover:text-slate-700 inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Customers
          </button>
          <ChevronRight className="h-3 w-3" />
          <span className="font-mono">{c.id}</span>
        </div>
        <div className="mt-3 flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-lg bg-slate-900 text-white text-xl font-semibold flex items-center justify-center">
              {c.name.split(' ').map((n) => n[0]).slice(0, 2).join('')}
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{c.name}</h1>
              <div className="mt-1 text-sm text-slate-500 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {c.address}, {c.city}</span>
                <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> <span className="font-mono">{c.phone}</span></span>
                <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> <span className="font-mono">{c.email}</span></span>
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <StatusBadge status={c.healthBand.toLowerCase()} />
                <StatusBadge status={c.plan === 'Premium Comfort Protection' ? 'warning' : c.plan === 'Preventive Maintenance' ? 'healthy' : 'info'}>{c.plan}</StatusBadge>
                <span className="text-xs font-mono text-slate-500">LTV ${c.lifetimeValue.toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline"><Phone className="h-4 w-4 mr-2" />Call</Button>
            <Button variant="outline"><Mail className="h-4 w-4 mr-2" />Email</Button>
            <Button className="bg-[#0052CC] hover:bg-[#0043A6]"><CalendarIcon className="h-4 w-4 mr-2" />Schedule Visit</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="p-5 border-slate-200 shadow-sm xl:col-span-2">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Installed Equipment</div>
          <div className="mt-3 space-y-2">
            {c.installedEquipment.map((eq, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-md border border-slate-200">
                <div>
                  <div className="font-medium text-slate-900 text-sm">{eq}</div>
                  <div className="text-[11px] text-slate-500 font-mono">Installed {2019 + (i % 4)} · Under warranty until {2028 + (i % 3)}</div>
                </div>
                <StatusBadge status="healthy">Active</StatusBadge>
              </div>
            ))}
          </div>
          <Separator className="my-5" />
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Connected Honeywell Devices</div>
          <div className="mt-3 space-y-2">
            {custDevices.map((d) => (
              <Link key={d.id} to={`/devices/${d.id}`} className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-slate-300 transition-colors">
                <div>
                  <div className="font-medium text-slate-900 text-sm">{d.model}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{d.id} · check-in {d.lastCheckIn}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm">{d.healthScore}/100</span>
                  <StatusBadge status={d.connection === 'offline' ? 'offline' : d.healthBand.toLowerCase()} />
                </div>
              </Link>
            ))}
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Maintenance Plan</div>
          {plan && (
            <div className="mt-3">
              <div className="text-lg font-semibold text-slate-900">{plan.tier}</div>
              <div className="mt-1 font-mono text-sm text-slate-500">${plan.priceMonthly}/mo · Active</div>
              <ul className="mt-3 space-y-1.5 text-xs text-slate-700">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 mt-1.5" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Separator className="my-5" />
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Preferred Technician</div>
          {tech && (
            <div className="mt-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">{tech.initials}</div>
              <div>
                <div className="font-medium text-slate-900 text-sm">{tech.name}</div>
                <div className="text-[11px] text-slate-500">{tech.role} · rating {tech.rating}</div>
              </div>
            </div>
          )}
          <Separator className="my-5" />
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Upcoming Appointments</div>
          <div className="mt-3 p-3 rounded-md bg-slate-50 border border-slate-200">
            <div className="font-medium text-sm text-slate-900">Preventive maintenance</div>
            <div className="mt-0.5 font-mono text-xs text-slate-500">{c.nextScheduledService} · 09:00 AM</div>
          </div>
          <div className="mt-5 text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Internal Notes</div>
          <div className="mt-2 text-sm text-slate-700 italic">"{c.notes}"</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Service History</div>
            <Button variant="ghost" size="sm"><FileText className="h-4 w-4 mr-1" />All records</Button>
          </div>
          <div className="mt-3 space-y-3">
            {custTickets.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">No service tickets yet.</div>}
            {custTickets.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-3 rounded-md border border-slate-200">
                <div>
                  <div className="font-medium text-slate-900 text-sm">{t.issue}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{t.id} · {t.scheduledAt}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm">${t.estRevenue}</span>
                  <StatusBadge status={t.status} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Alerts Tied to This Customer</div>
          <div className="mt-3 space-y-3">
            {custAlerts.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">No active alerts.</div>}
            {custAlerts.map((a) => (
              <div key={a.id} className="flex items-start gap-3 p-3 rounded-md border border-slate-200">
                <AlertTriangle className={`h-4 w-4 mt-0.5 ${a.severity === 'critical' ? 'text-red-500' : a.severity === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} />
                <div className="flex-1">
                  <div className="font-medium text-slate-900 text-sm">{a.type}</div>
                  <div className="text-[11px] text-slate-500">{a.createdAgo}</div>
                  <div className="mt-1 text-xs text-slate-600">{a.recommended}</div>
                </div>
                <StatusBadge status={a.severity} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Invoices</div>
          <div className="mt-3 divide-y divide-slate-100">
            {invoices.map((inv) => (
              <div key={inv.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-mono text-xs text-slate-500">{inv.id} · {inv.date}</div>
                  <div className="text-sm text-slate-900">{inv.desc}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm">${inv.amount}</span>
                  <StatusBadge status="healthy">{inv.status}</StatusBadge>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Recommended Upsells</div>
          <div className="mt-3 space-y-2">
            {upsells.map((u) => {
              const Icon = u.icon;
              return (
                <div key={u.label} className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-[#0052CC] transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-md bg-blue-50 text-[#0052CC] flex items-center justify-center"><Icon className="h-4 w-4" /></div>
                    <div className="text-sm font-medium text-slate-900">{u.label}</div>
                  </div>
                  <span className="font-mono text-sm text-slate-600">{u.value}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
